import { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { Building2, WifiOff, Wallet, Receipt, CalendarDays, Plus, CheckCircle2, Paperclip, FileText, ClipboardList, Trash2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { crc, money, fmtDate, rollForward } from "./ClientesModule";
import ReciboCobro, { fechaCobro } from "./ReciboCobro";
import ManualRecibo from "./ManualRecibo";
import RecibosHistorial from "./RecibosHistorial";

const METODOS_PAGO = [
  { v: "transferencia", label: "Transferencia" },
  { v: "sinpe", label: "SINPE Móvil" },
  { v: "efectivo", label: "Efectivo" },
];
const metodoLabel = (m) => (METODOS_PAGO.find((x) => x.v === m) || {}).label || "—";
const mesActual = () => new Date().toISOString().slice(0, 7);
const fmtMes = (m) => {
  const s = new Date(m + "-02T00:00:00").toLocaleDateString("es-CR", { month: "long", year: "numeric" });
  return s.charAt(0).toUpperCase() + s.slice(1);
};

const sysBadge = {
  rrhh: { label: "Sistema RH", cls: "bg-vteal-light/15 text-vteal-light border-vteal-light/30" },
  legal: { label: "Despacho Legal", cls: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  manual: { label: "Recibo Manual", cls: "bg-sky-500/15 text-sky-300 border-sky-500/30" },
};

function SummaryCard({ icon: Icon, label, value, note, testid }) {
  return (
    <div className="rounded-xl bg-vnavy-800 border border-white/10 p-5" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="h-4 w-4 text-vteal-light" />
        <span className="text-[11px] uppercase tracking-[0.15em] text-gray-500 font-semibold">{label}</span>
      </div>
      <p className="font-serif text-2xl text-white">{value}</p>
      {note && <p className="text-gray-500 text-xs mt-1">{note}</p>}
    </div>
  );
}

function MiniField({ label, value, tone = "text-gray-200" }) {
  return (
    <div className="rounded-lg bg-vnavy border border-white/5 px-4 py-2.5">
      <p className="text-[10px] uppercase tracking-[0.15em] text-gray-500 mb-0.5">{label}</p>
      <p className={`${tone} text-sm font-semibold truncate`} title={value}>{value}</p>
    </div>
  );
}

function FeeCard({ client, billing, cobros = [], onRecibo, onRegistrarCobro }) {
  const amount = billing?.amount || 0;
  const porCobro = amount
    ? billing.frequency === "quincenal"
      ? (billing.invoice ? (amount * 1.13) / 2 : amount / 2)
      : (billing.invoice ? amount * 1.13 : amount)
    : 0;
  const proximo = billing?.pay_date_1
    ? `${fmtDate(rollForward(billing.pay_date_1))}${billing.pay_date_2 ? ` · ${fmtDate(rollForward(billing.pay_date_2))}` : ""}`
    : "—";
  const sb = sysBadge[client.system];
  return (
    <div className="rounded-xl bg-vnavy-800 border border-white/10 p-5" data-testid={`cobro-fee-${client.system}-${client.id}`}>
      <div className="flex items-center gap-3 flex-wrap mb-4">
        <div className="h-9 w-9 rounded-lg bg-vteal/15 flex items-center justify-center shrink-0">
          <Building2 className="h-4 w-4 text-vteal-light" />
        </div>
        <h3 className="text-white font-semibold">{client.company_name}</h3>
        <Badge className={`border ${sb.cls}`}>{sb.label}</Badge>
        {!billing?.amount && <Badge className="border bg-amber-500/15 text-amber-300 border-amber-500/30">Plan de cobro sin configurar</Badge>}
        {billing?.amount > 0 && (
          <div className="ml-auto flex items-center gap-2">
            <button onClick={() => onRecibo(client, billing)} data-testid={`recibo-btn-${client.system}-${client.id}`}
              className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border border-vteal-light/30 text-vteal-light hover:bg-vteal-light/10 transition-colors">
              <Receipt className="h-3.5 w-3.5" /> Recibo
            </button>
            <button onClick={() => onRegistrarCobro(client, billing)} data-testid={`registrar-cobro-btn-${client.system}-${client.id}`}
              className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border border-green-500/40 text-green-300 hover:bg-green-500/10 transition-colors">
              <CheckCircle2 className="h-3.5 w-3.5" /> Registrar cobro
            </button>
          </div>
        )}
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <MiniField label={`Mensualidad${billing?.currency === "USD" ? " ($)" : ""}`} value={amount ? money(amount, billing?.currency) : "—"} />
        <MiniField
          label={billing?.frequency === "quincenal" ? "Por cobro (quincenal)" : "Por cobro (mensual)"}
          value={amount ? `${money(porCobro, billing?.currency)}${billing?.invoice ? " c/IVA" : ""}` : "—"}
          tone="text-vteal-light"
        />
        <MiniField label="Próximo cobro" value={proximo} tone="text-amber-300" />
        <MiniField label="Factura electrónica" value={billing?.invoice ? "Sí · +13% IVA" : "No"} />
        {billing?.client_code && <MiniField label="Código facturación" value={billing.client_code} />}
      </div>
      {cobros.length > 0 && (
        <p className="text-gray-500 text-xs mt-3" data-testid={`ultimo-cobro-${client.system}-${client.id}`}>
          Último cobro registrado: <span className="text-green-300 font-semibold">{fmtDate(cobros[0].fecha_cobro)} · {money(cobros[0].monto, cobros[0].currency)}</span>
        </p>
      )}
    </div>
  );
}

function SistemaCard({ client, billing, onRegistrarCobro }) {
  return (
    <div className="rounded-xl bg-vnavy-800 border border-white/10 p-5" data-testid={`cobro-sys-${client.id}`}>
      <div className="flex items-center gap-3 flex-wrap mb-4">
        <div className="h-9 w-9 rounded-lg bg-vteal/15 flex items-center justify-center shrink-0">
          <Building2 className="h-4 w-4 text-vteal-light" />
        </div>
        <h3 className="text-white font-semibold">{client.company_name}</h3>
        <span className="text-xs text-gray-500 uppercase tracking-wide">{client.plan} · {client.frequency}</span>
        {billing?.amount > 0 && (
          <button onClick={() => onRegistrarCobro(client, billing)} data-testid={`registrar-cobro-btn-${client.system}-${client.id}`}
            className="ml-auto flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border border-green-500/40 text-green-300 hover:bg-green-500/10 transition-colors">
            <CheckCircle2 className="h-3.5 w-3.5" /> Registrar cobro
          </button>
        )}
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <MiniField label="Total del ciclo" value={crc(client.cycle_total)} tone="text-vteal-light" />
        <MiniField label="Próximo pago" value={fmtDate(client.next_payment_date)} tone="text-amber-300" />
        <MiniField label="Último pago" value={client.last_payment_date ? `${fmtDate(client.last_payment_date)} · ${crc(client.last_payment_amount)}` : "Sin pagos"} />
        <MiniField label="Total pagado" value={crc(client.total_paid)} />
      </div>
    </div>
  );
}

function ConnError({ system, error }) {
  return (
    <div className="mb-6 rounded-xl border border-amber-500/30 bg-amber-500/10 p-5 flex items-start gap-3" data-testid={`cobros-conn-error-${system}`}>
      <WifiOff className="h-5 w-5 text-amber-300 shrink-0 mt-0.5" />
      <div>
        <p className="text-amber-200 font-semibold text-sm">No se pudo conectar con el sistema {system === "rrhh" ? "de Recursos Humanos" : "de Despacho Legal"}.</p>
        <p className="text-amber-200/70 text-xs mt-1">{error}</p>
      </div>
    </div>
  );
}

function EmptyNote({ children }) {
  return (
    <div className="text-center py-16 text-gray-500">
      <Wallet className="h-10 w-10 mx-auto mb-3 opacity-40" />
      {children}
    </div>
  );
}

function RegistrarCobroDialog({ client, billing, open, onClose, onRegistered }) {
  const esQuincenal = billing?.frequency === "quincenal";
  const [quincena, setQuincena] = useState("1");
  const [fechaSel, setFechaSel] = useState("");
  const [metodo, setMetodo] = useState("");
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && billing) {
      setQuincena("1");
      setFechaSel(rollForward(billing.pay_date_1) || "");
      setMetodo("");
      setFile(null);
    }
  }, [open, billing]);

  if (!client || !billing) return null;

  const porCobro = esQuincenal ? billing.amount / 2 : billing.amount;
  const monto = billing.invoice ? porCobro * 1.13 : porCobro;

  const elegirQuincena = (q) => {
    setQuincena(q);
    const base = q === "2" ? billing.pay_date_2 : billing.pay_date_1;
    setFechaSel(rollForward(base) || "");
  };

  const submit = async () => {
    if (!fechaSel) { toast.error("Elija la fecha del período de cobro."); return; }
    if (!metodo) { toast.error("Elija el método de pago."); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("fecha_cobro", fechaSel);
      fd.append("metodo_pago", metodo);
      fd.append("quincena", esQuincenal ? quincena : "");
      fd.append("company", client.company_name || "");
      if (file) fd.append("comprobante", file);
      const { data } = await api.post(`/admin/billing/${client.system}/${client.id}/cobrado`, fd);
      onRegistered(client.system, client.id, data);
      toast.success(`Cobro registrado: ${money(data.monto, data.currency)} · ${fmtDate(data.fecha_cobro)} · ${metodoLabel(data.metodo_pago)}`);
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo registrar el cobro.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-vnavy-800 border-white/10 text-white max-w-md" data-testid="registrar-cobro-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif">Registrar cobro</DialogTitle>
          <DialogDescription className="text-gray-400">
            {client.company_name} · <span className="text-vteal-light font-semibold">{money(monto, billing.currency)}</span>{billing.invoice ? " (IVA incluido)" : ""}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {esQuincenal && (
            <div>
              <p className="text-[10px] uppercase tracking-[0.15em] text-gray-500 mb-1.5">¿A cuál quincena corresponde? *</p>
              <div className="flex rounded-lg overflow-hidden border border-white/10">
                {[{ v: "1", label: `1ª quincena · ${fmtDate(rollForward(billing.pay_date_1))}` }, { v: "2", label: `2ª quincena · ${fmtDate(rollForward(billing.pay_date_2))}` }].map((q) => (
                  <button key={q.v} type="button" onClick={() => elegirQuincena(q.v)} data-testid={`cobro-quincena-${q.v}`}
                    className={`flex-1 text-xs py-2.5 px-2 font-semibold transition-colors ${quincena === q.v ? "bg-vteal-light text-vnavy" : "text-gray-400 hover:text-white"}`}>
                    {q.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <p className="text-[10px] uppercase tracking-[0.15em] text-gray-500 mb-1.5">Período de cobro *</p>
            <input type="date" value={fechaSel} onChange={(e) => setFechaSel(e.target.value)} data-testid="cobro-fecha"
              className="w-full h-9 rounded-md border border-white/15 bg-vnavy px-3 text-sm text-white [color-scheme:dark] focus:outline-none focus:ring-2 focus:ring-vteal-light/50" />
            <p className="text-gray-600 text-[10px] mt-1">Se sugiere la próxima fecha del plan; puede cambiarla.</p>
          </div>

          <div>
            <p className="text-[10px] uppercase tracking-[0.15em] text-gray-500 mb-1.5">Método de pago *</p>
            <div className="flex rounded-lg overflow-hidden border border-white/10">
              {METODOS_PAGO.map((m) => (
                <button key={m.v} type="button" onClick={() => setMetodo(m.v)} data-testid={`cobro-metodo-${m.v}`}
                  className={`flex-1 text-xs py-2.5 px-1 font-semibold transition-colors ${metodo === m.v ? "bg-vteal-light text-vnavy" : "text-gray-400 hover:text-white"}`}>
                  {m.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="text-[10px] uppercase tracking-[0.15em] text-gray-500 mb-1.5">Comprobante de pago (PDF o imagen, opcional)</p>
            <label className="flex items-center gap-2 rounded-lg border border-dashed border-white/20 px-3 py-3 cursor-pointer hover:border-vteal-light/60 transition-colors">
              <Paperclip className="h-4 w-4 text-vteal-light shrink-0" />
              <span className="text-sm text-gray-300 truncate">{file ? file.name : "Adjuntar comprobante del cliente..."}</span>
              <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" data-testid="cobro-comprobante"
                onChange={(e) => setFile(e.target.files?.[0] || null)} />
            </label>
          </div>

          <button onClick={submit} disabled={saving} data-testid="cobro-submit"
            className="w-full flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-vteal-light text-vnavy text-sm font-semibold hover:bg-white transition-colors disabled:opacity-60">
            <CheckCircle2 className="h-4 w-4" /> {saving ? "Registrando..." : "Registrar cobro"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RegistroCobros() {
  const [month, setMonth] = useState(mesActual());
  const [cobros, setCobros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openingFile, setOpeningFile] = useState("");
  const [confirmDelete, setConfirmDelete] = useState("");
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/cobros/registro?month=${month}`);
      setCobros(data || []);
    } catch {
      toast.error("No se pudo cargar el registro de cobros.");
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => { load(); }, [load]);

  const grupos = useMemo(() => {
    const map = {};
    cobros.forEach((c) => { (map[c.fecha_cobro] = map[c.fecha_cobro] || []).push(c); });
    return Object.entries(map).sort((a, b) => b[0].localeCompare(a[0]));
  }, [cobros]);

  const verComprobante = async (c) => {
    setOpeningFile(c.id);
    try {
      const resp = await api.get(`/admin/cobros/${c.id}/comprobante`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      toast.error("No se pudo abrir el comprobante.");
    } finally {
      setOpeningFile("");
    }
  };

  const remove = async (c) => {
    if (confirmDelete !== c.id) {
      setConfirmDelete(c.id);
      setTimeout(() => setConfirmDelete((x) => (x === c.id ? "" : x)), 4000);
      return;
    }
    setDeleting(true);
    try {
      await api.delete(`/admin/cobros/${c.id}`);
      setCobros((prev) => prev.filter((x) => x.id !== c.id));
      toast.success(`Cobro de ${c.company || "cliente"} eliminado del registro.${c.receipt_numero ? " El recibo vuelve a Pendiente de pago." : ""}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo eliminar el cobro.");
    } finally {
      setDeleting(false);
      setConfirmDelete("");
    }
  };

  const sumPorMoneda = (lista) => {
    const t = { CRC: 0, USD: 0 };
    lista.forEach((c) => { t[c.currency === "USD" ? "USD" : "CRC"] += c.monto || 0; });
    return [t.CRC ? money(t.CRC, "CRC") : "", t.USD ? money(t.USD, "USD") : ""].filter(Boolean).join(" + ") || money(0, "CRC");
  };

  return (
    <div data-testid="registro-cobros">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <input type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)}
          data-testid="registro-month"
          className="h-9 rounded-md border border-white/15 bg-vnavy px-3 text-sm text-white [color-scheme:dark] focus:outline-none focus:ring-2 focus:ring-vteal-light/50" />
        <div className="rounded-lg bg-vnavy-800 border border-white/10 px-4 py-2" data-testid="registro-total">
          <span className="text-[10px] uppercase tracking-[0.15em] text-gray-500 mr-2">Total {fmtMes(month)}</span>
          <span className="text-vteal-light font-semibold">{sumPorMoneda(cobros)}</span>
        </div>
      </div>

      {loading ? (
        <p className="text-gray-500 py-12 text-center">Cargando registro...</p>
      ) : grupos.length === 0 ? (
        <EmptyNote>No hay cobros registrados en {fmtMes(month)}.</EmptyNote>
      ) : (
        <div className="space-y-4" data-testid="registro-list">
          {grupos.map(([fecha, lista]) => (
            <div key={fecha} className="rounded-xl bg-vnavy-800 border border-white/10 overflow-hidden" data-testid={`registro-day-${fecha}`}>
              <div className="flex items-center justify-between px-5 py-3 border-b border-white/5 bg-vnavy/60">
                <p className="text-white text-sm font-semibold">{fmtDate(fecha)}</p>
                <p className="text-gray-400 text-xs">{lista.length} cobro{lista.length !== 1 ? "s" : ""} · <span className="text-vteal-light font-semibold">{sumPorMoneda(lista)}</span></p>
              </div>
              <div className="divide-y divide-white/5">
                {lista.map((c) => {
                  const sb = sysBadge[c.system] || { label: c.system, cls: "bg-white/5 text-gray-400 border-white/10" };
                  return (
                    <div key={c.id} className="flex items-center gap-3 px-5 py-3 flex-wrap" data-testid={`registro-row-${c.id}`}>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-white text-sm font-medium truncate">{c.company || "Cliente"}</p>
                          <Badge className={`border ${sb.cls}`}>{sb.label}</Badge>
                          {c.quincena && <Badge className="border bg-white/5 text-gray-300 border-white/10">{c.quincena === "1" ? "1ª" : "2ª"} quincena</Badge>}
                        </div>
                        <p className="text-gray-500 text-xs mt-1">{metodoLabel(c.metodo_pago)}{c.receipt_numero ? ` · N° ${c.receipt_numero}` : ""}</p>
                      </div>
                      <p className="text-white font-semibold shrink-0">{money(c.monto, c.currency)}</p>
                      {c.comprobante_path ? (
                        <button onClick={() => verComprobante(c)} disabled={openingFile === c.id} data-testid={`registro-comprobante-${c.id}`}
                          className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border border-vteal-light/30 text-vteal-light hover:bg-vteal-light/10 transition-colors disabled:opacity-60 shrink-0">
                          <FileText className="h-3.5 w-3.5" /> {openingFile === c.id ? "Abriendo..." : "Comprobante"}
                        </button>
                      ) : (
                        <span className="text-gray-600 text-xs shrink-0">Sin comprobante</span>
                      )}
                      <button onClick={() => remove(c)} disabled={deleting} data-testid={`registro-delete-${c.id}`} title="Eliminar del registro"
                        className={`flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border transition-colors disabled:opacity-60 shrink-0 ${confirmDelete === c.id ? "border-red-500 bg-red-500/20 text-red-200 font-semibold" : "border-white/15 text-gray-400 hover:border-red-500/50 hover:text-red-300"}`}>
                        <Trash2 className="h-3.5 w-3.5" /> {confirmDelete === c.id ? "¿Confirmar?" : ""}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function CentroCobrosModule() {
  const [sysData, setSysData] = useState({
    rrhh: { clients: [], billing: {}, cobros: {}, connected: true, error: null },
    legal: { clients: [], billing: {}, cobros: {}, connected: true, error: null },
  });
  const [loading, setLoading] = useState(true);
  const [recibo, setRecibo] = useState(null); // { client, billing }
  const [cobroForm, setCobroForm] = useState(null); // { client, billing }

  const load = useCallback(async () => {
    setLoading(true);
    const result = {};
    await Promise.all(
      ["rrhh", "legal"].map(async (system) => {
        try {
          const [{ data }, bill, cob] = await Promise.all([
            api.get(`/admin/clients?system=${system}`),
            api.get(`/admin/billing?system=${system}`),
            api.get(`/admin/cobros?system=${system}`),
          ]);
          const map = {};
          (bill.data || []).forEach((b) => { map[b.client_id] = b; });
          const cmap = {};
          (cob.data || []).forEach((cb) => { (cmap[cb.client_id] = cmap[cb.client_id] || []).push(cb); });
          result[system] = {
            clients: data.clients || [],
            billing: map,
            cobros: cmap,
            connected: data.connected,
            error: data.error,
          };
        } catch (err) {
          result[system] = {
            clients: [],
            billing: {},
            cobros: {},
            connected: false,
            error: formatApiErrorDetail(err.response?.data?.detail) || "Error de conexión.",
          };
        }
      })
    );
    setSysData((prev) => ({ ...prev, ...result }));
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const abrirRegistroCobro = (client, billing) => {
    const { fecha } = fechaCobro(billing);
    if (!fecha) {
      toast.error("Configure la fecha de pago en el plan de cobro primero.");
      return;
    }
    setCobroForm({ client, billing });
  };

  const onCobroRegistrado = (system, clientId, data) => {
    setSysData((prev) => ({
      ...prev,
      [system]: {
        ...prev[system],
        cobros: { ...prev[system].cobros, [clientId]: [data, ...(prev[system].cobros[clientId] || [])] },
      },
    }));
  };

  const activos = (s) => sysData[s].clients.filter((c) => c.registration_status !== "pending");
  const feeItems = ["rrhh", "legal"]
    .flatMap((s) => activos(s).filter((c) => c.is_free).map((c) => ({ ...c, system: s, billing: sysData[s].billing[c.id], cobros: sysData[s].cobros[c.id] || [] })))
    .sort((a, b) => Number(!!b.billing?.amount) - Number(!!a.billing?.amount) || a.company_name.localeCompare(b.company_name));
  const sysClients = (s) => activos(s).filter((c) => !c.is_free);

  const sumFee = (fn) => {
    const t = { CRC: 0, USD: 0 };
    feeItems.forEach((c) => { t[c.billing?.currency === "USD" ? "USD" : "CRC"] += fn(c); });
    return [t.CRC ? crc(t.CRC) : "", t.USD ? money(t.USD, "USD") : ""].filter(Boolean).join(" + ") || crc(0);
  };
  const feeTotalStr = sumFee((c) => c.billing?.amount || 0);
  const feeTotalConIvaStr = sumFee((c) => (c.billing?.amount ? c.billing.amount * (c.billing.invoice ? 1.13 : 1) : 0));
  const feeConPlan = feeItems.filter((c) => c.billing?.amount).length;
  const sysTotal = ["rrhh", "legal"].reduce((acc, s) => acc + sysClients(s).reduce((a, c) => a + (c.cycle_total || 0), 0), 0);
  const sysCount = ["rrhh", "legal"].reduce((acc, s) => acc + sysClients(s).length, 0);

  return (
    <div>
      <div className="grid sm:grid-cols-2 gap-4 mb-8">
        <SummaryCard
          icon={Receipt}
          label="Mensualidades · Clientes Fee"
          value={feeTotalStr}
          note={`${feeConPlan} de ${feeItems.length} con plan de cobro · Con IVA: ${feeTotalConIvaStr}`}
          testid="cobros-total-fee"
        />
        <SummaryCard
          icon={CalendarDays}
          label="Suscripciones · Sistemas RH y Legal"
          value={crc(sysTotal)}
          note={`${sysCount} empresa${sysCount !== 1 ? "s" : ""} con suscripción activa`}
          testid="cobros-total-sistemas"
        />
      </div>

      <Tabs defaultValue="fee">
        <TabsList className="bg-vnavy border border-white/10 mb-6">
          <TabsTrigger value="fee" data-testid="cobros-tab-fee" className="data-[state=active]:bg-vteal-light data-[state=active]:text-vnavy">
            <Receipt className="h-3.5 w-3.5 mr-1.5" /> Clientes Fee
          </TabsTrigger>
          <TabsTrigger value="sistemas" data-testid="cobros-tab-sistemas" className="data-[state=active]:bg-vteal-light data-[state=active]:text-vnavy">
            <CalendarDays className="h-3.5 w-3.5 mr-1.5" /> Clientes Sistemas
          </TabsTrigger>
          <TabsTrigger value="manual" data-testid="cobros-tab-manual" className="data-[state=active]:bg-vteal-light data-[state=active]:text-vnavy">
            <Plus className="h-3.5 w-3.5 mr-1.5" /> Recibo Manual
          </TabsTrigger>
          <TabsTrigger value="recibos" data-testid="cobros-tab-recibos" className="data-[state=active]:bg-vteal-light data-[state=active]:text-vnavy">
            <FileText className="h-3.5 w-3.5 mr-1.5" /> Historial Recibos
          </TabsTrigger>
          <TabsTrigger value="registro" data-testid="cobros-tab-registro" className="data-[state=active]:bg-vteal-light data-[state=active]:text-vnavy">
            <ClipboardList className="h-3.5 w-3.5 mr-1.5" /> Registro de Cobros
          </TabsTrigger>
        </TabsList>
        <TabsContent value="fee">
          {loading ? (
            <p className="text-gray-500 py-12 text-center">Cargando clientes fee...</p>
          ) : (
            <div>
              {["rrhh", "legal"].map((s) => (!sysData[s].connected && sysData[s].error ? <ConnError key={s} system={s} error={sysData[s].error} /> : null))}
              {feeItems.length === 0 ? (
                <EmptyNote>No hay empresas Fee. Active el check "Empresa Fee" en la sección Clientes.</EmptyNote>
              ) : (
                <div className="space-y-4" data-testid="cobros-fee-list">
                  {feeItems.map((c) => (
                    <FeeCard key={`${c.system}-${c.id}`} client={c} billing={c.billing} cobros={c.cobros}
                      onRecibo={(client, billing) => setRecibo({ client, billing })}
                      onRegistrarCobro={abrirRegistroCobro} />
                  ))}
                </div>
              )}
            </div>
          )}
        </TabsContent>
        <TabsContent value="sistemas">
          <Tabs defaultValue="rrhh">
            <TabsList className="bg-vnavy border border-white/10 mb-6">
              <TabsTrigger value="rrhh" data-testid="cobros-sys-rrhh" className="data-[state=active]:bg-vteal-light data-[state=active]:text-vnavy">
                Sistema RH
              </TabsTrigger>
              <TabsTrigger value="legal" data-testid="cobros-sys-legal" className="data-[state=active]:bg-vteal-light data-[state=active]:text-vnavy">
                Sistema Despacho Legal
              </TabsTrigger>
            </TabsList>
            {["rrhh", "legal"].map((s) => (
              <TabsContent key={s} value={s}>
                {loading ? (
                  <p className="text-gray-500 py-12 text-center">Cargando clientes...</p>
                ) : (
                  <div>
                    {!sysData[s].connected && sysData[s].error && <ConnError system={s} error={sysData[s].error} />}
                    {sysClients(s).length === 0 ? (
                      <EmptyNote>No hay clientes del sistema con cobros activos.</EmptyNote>
                    ) : (
                      <div className="space-y-4" data-testid={`cobros-sistema-list-${s}`}>
                        {sysClients(s).map((c) => <SistemaCard key={c.id} client={{ ...c, system: s }} billing={sysData[s].billing[c.id]} onRegistrarCobro={abrirRegistroCobro} />)}
                      </div>
                    )}
                  </div>
                )}
              </TabsContent>
            ))}
          </Tabs>
        </TabsContent>
        <TabsContent value="manual">
          <ManualRecibo />
        </TabsContent>
        <TabsContent value="recibos">
          <RecibosHistorial />
        </TabsContent>
        <TabsContent value="registro">
          <RegistroCobros />
        </TabsContent>
      </Tabs>

      <ReciboCobro client={recibo?.client} billing={recibo?.billing} open={!!recibo} onClose={() => setRecibo(null)} />
      <RegistrarCobroDialog client={cobroForm?.client} billing={cobroForm?.billing} open={!!cobroForm} onClose={() => setCobroForm(null)} onRegistered={onCobroRegistrado} />
    </div>
  );
}
